import type { HoldTransitionEvent } from '../domain/chrome';
import { dispatch } from './bus';
import type { DispatchOptions, DispatchResult } from './bus';

/**
 * SES-1's two dispatch shapes, as the chrome's only route into the state machine.
 *
 * There is deliberately nothing else in this module. Every rule — which transitions are legal, the
 * anchor's validity window, the outbox row, the `activeMatch` pointer — belongs to
 * `domain/machine.ts` and `domain/commands/transition.ts`; a second place that decided any of them
 * would be a second transition table. What is here is the distinction the *gesture* makes, and it is
 * the one thing a UI can get wrong on its own:
 *
 * | | Gesture | Carries `anchorTs` | Why |
 * |---|---|---|---|
 * | {@link runHoldTransition} | confirming hold, `TRANSITION_HOLD_MS` | **yes — press-start** | SES-1 defines no reverse transition, so a mis-tap corrupts every minute derived afterwards |
 * | {@link abandonMatch} | **single tap** behind `⋯`, with a confirm | **no** | SES-6/§2.2: Abandon is not a clock-anchoring transition; the handler writes `fullTimeTs = now` |
 *
 * Getting that asymmetry backwards is not a cosmetic error in either direction. A hold on Abandon
 * would put a gesture contract on a control §6.4 keeps off the in-play surface entirely; an
 * `anchorTs` on Abandon would be a press-start time for a press that anchors nothing.
 *
 * ── The `options` parameter is U-4's seam, not a new one ─────────────────────
 *
 * `DispatchOptions.now` exists already and `bus.ts` says why: *"Wall clock for the snapshot's `now`.
 * Injected so SES-1's anchor window is testable."* SES-1's anchor window is exactly what this module
 * feeds, so the parameter is forwarded rather than a second clock being invented behind it. In
 * production both call sites omit it and the bus takes `Date.now`.
 */

/**
 * One of SES-1's four confirming-hold transitions.
 *
 * @param anchorTs **the moment the press began**, captured at `pointerdown` and carried through
 *   untouched. This function reads no clock — `machine.transition` validates the value against its
 *   own `now` and rejects outside `[0, TRANSITION_HOLD_MS + HOLD_SLOP_MS]`, so a stale or forged
 *   anchor cannot enter the clock. A caller that passed a completion time here would be inventing
 *   the exact error the hold exists to prevent, which is why it is a required parameter with no
 *   default rather than something this module could helpfully supply.
 */
export function runHoldTransition(
  matchId: string,
  event: HoldTransitionEvent,
  anchorTs: number,
  options: DispatchOptions = {},
): Promise<DispatchResult> {
  return dispatch({ type: 'Transition', event, matchId, anchorTs }, options);
}

/**
 * SES-6 — Abandon, from any in-match state including halftime.
 *
 * No `anchorTs`: §2.2's row sources this anchor from `now`, inside the transaction. All data is
 * retained, the match lands in `fullTime` with `abandoned = true`, and SYN-1's push fires — so an
 * abandoned match reaches the backup service rather than existing only on the device.
 */
export function abandonMatch(matchId: string, options: DispatchOptions = {}): Promise<DispatchResult> {
  return dispatch({ type: 'Transition', event: 'Abandon', matchId }, options);
}
